import { ChatMessage } from "@/app/api/chats/[chatId]/messages/messages.dto";
import { cn } from "@/lib/utils";
import { deleteMessage } from "@/services/chat/messages";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2Icon } from "lucide-react";

const DeleteMessageButton = ({
	messageId,
	chatId,
}: {
	messageId: string;
	chatId: string | undefined;
}) => {
	const queryClient = useQueryClient();

	const handleDeleteMessage = async (messageId: string) => {
		await deleteMessage({ messageId });
	};

	const deleteMessageMutation = useMutation(handleDeleteMessage, {
		onMutate: async (messageId: string) => {
			await queryClient.cancelQueries(["chatMessages", chatId]);

			const previousMessages = queryClient.getQueryData<ChatMessage[]>(["chatMessages", chatId]);

			queryClient.setQueryData<ChatMessage[]>(["chatMessages", chatId], (oldMessages) =>
				oldMessages?.filter((message) => message.id !== messageId)
			);

			return { previousMessages };
		},
		onError: (_, __, context) => {
			queryClient.setQueryData(["chatMessages", chatId], context?.previousMessages);
		},
		onSettled: () => {
			queryClient.invalidateQueries(["chatMessages", chatId]);
			queryClient.refetchQueries(["chatMessages", chatId]);
		},
	});

	return (
		<button
			type="button"
			disabled={deleteMessageMutation.isLoading}
			onClick={() => deleteMessageMutation.mutate(messageId)}
			className={cn(
				"invisible absolute left-[-30px] top-[-16px] rounded-md p-1 transition-all duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-50 group-hover:visible"
			)}
		>
			<Trash2Icon className="h-4 w-4 text-red-500" />
		</button>
	);
};

export default DeleteMessageButton;
